import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Check, Loader2, Plus, Trash2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Button, Card, Field, Input, Select, cx } from '../ui/kit'

/**
 * Der Online-Check-in, aus Sicht des Gasts.
 *
 * Diese Seite ist die einzige in StaySuite, die ein Gast je zu sehen bekommt, und er
 * öffnet sie auf dem Handy, vermutlich unterwegs, vermutlich einmal. Deshalb: keine
 * Anmeldung, keine Erklärung vorweg, ein Formular und ein Knopf.
 *
 * Die Sprache folgt dem Betrieb, nicht dem Browser — der Gast hat die Einladung vom
 * Vermieter bekommen, und wenn die auf Deutsch war, ist die Seite es auch. Für
 * englischsprachige Gäste schickt der Vermieter die englische Einladung.
 *
 * WARUM SO VIELE FELDER: das Meldegesetz verlangt sie, je Person. Wir fragen nicht aus
 * Neugier — und die Seite sagt das auch, weil ein Formular, das nach Ausweisnummern
 * fragt, ohne Begründung wie ein Betrugsversuch aussieht.
 */

interface Person {
  first_name: string
  last_name: string
  birth_date: string
  nationality: string
  street: string
  postcode: string
  city: string
  country: string
  document_type: string
  document_number: string
}

const emptyPerson = (): Person => ({
  first_name: '', last_name: '', birth_date: '', nationality: '',
  street: '', postcode: '', city: '', country: '',
  document_type: '', document_number: '',
})

interface Loaded {
  status: 'open' | 'submitted' | 'checked_in'
  booking: {
    reference: string
    arrival: string
    departure: string
    adults: number
    children: number
    unit: string
  }
  host: {
    name: string; street: string; postcode: string; city: string
    email: string; phone: string; locale: string
  }
  arrival_time: string
  note: string
  people: Partial<Person>[]
}

export default function CheckinView() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<Loaded | null>(null)
  const [people, setPeople] = useState<Person[]>([emptyPerson()])
  const [arrivalTime, setArrivalTime] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const de = (data?.host.locale ?? 'de').startsWith('de')

  const call = useCallback(async (action: string, extra: Record<string, unknown> = {}) => {
    if (!supabase) throw new Error('no_backend')
    const { data: out, error: e } = await supabase.functions.invoke('checkin', {
      body: { action, token, ...extra },
    })
    if (e) {
      const res = (e as { context?: Response }).context
      if (res && typeof res.json === 'function') {
        const parsed = await res.json().catch(() => null) as { error?: string } | null
        if (parsed?.error) throw new Error(parsed.error)
      }
      throw new Error(e.message)
    }
    return out as Record<string, unknown>
  }, [token])

  useEffect(() => {
    void (async () => {
      try {
        const out = await call('load') as unknown as Loaded
        setData(out)
        setArrivalTime(out.arrival_time || '')
        setNote(out.note || '')
        if (out.people.length > 0) {
          setPeople(out.people.map((p) => ({ ...emptyPerson(), ...p } as Person)))
        } else {
          // So viele Zeilen wie gebuchte Personen, damit niemand rätselt, wen wir meinen.
          const n = Math.max(1, (out.booking.adults || 1) + (out.booking.children || 0))
          setPeople(Array.from({ length: Math.min(n, 10) }, emptyPerson))
        }
        if (out.status !== 'open') setDone(true)
      } catch (e) {
        setError(String((e as Error).message))
      }
    })()
  }, [call])

  if (error && !data) {
    return (
      <Shell>
        <Card className="p-4">
          <p className="text-sm text-danger">
            {error === 'not_found'
              ? 'Dieser Link ist nicht (mehr) gültig. Bitte wenden Sie sich an Ihren Gastgeber.'
              : `Es hat nicht geklappt (${error}).`}
          </p>
        </Card>
      </Shell>
    )
  }

  if (!data) {
    return (
      <Shell>
        <div className="p-8 text-center text-muted">
          <Loader2 className="animate-spin mx-auto" />
        </div>
      </Shell>
    )
  }

  const fmt = (iso: string) => new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(`${iso}T00:00:00`))

  if (done) {
    return (
      <Shell host={data.host.name}>
        <Card className="p-5 text-center space-y-2">
          <Check className="mx-auto text-ok" size={32} />
          <h1 className="font-semibold">
            {de ? 'Danke, wir haben alles' : 'Thank you, we have everything'}
          </h1>
          <p className="text-sm text-muted">
            {de
              ? `Ihr Meldeschein für ${fmt(data.booking.arrival)} – ${fmt(data.booking.departure)} ist eingegangen. Sie müssen bei der Ankunft nichts mehr ausfüllen.`
              : `Your registration for ${fmt(data.booking.arrival)} – ${fmt(data.booking.departure)} has been received. Nothing left to fill in on arrival.`}
          </p>
        </Card>
      </Shell>
    )
  }

  const setPerson = (i: number, patch: Partial<Person>) =>
    setPeople(people.map((p, j) => (j === i ? { ...p, ...patch } : p)))

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await call('submit', { people, arrival_time: arrivalTime, note })
      setDone(true)
    } catch (e) {
      const code = String((e as Error).message)
      setError(code === 'name_required'
        ? (de ? 'Bitte tragen Sie zumindest den Namen der ersten Person ein.'
              : 'Please enter at least the first traveller’s name.')
        : (de ? `Es hat nicht geklappt (${code}).` : `That did not work (${code}).`))
      setBusy(false)
    }
  }

  return (
    <Shell host={data.host.name}>
      <Card className="p-4 space-y-1">
        <h1 className="font-semibold">{de ? 'Online-Check-in' : 'Online check-in'}</h1>
        <p className="text-sm text-muted">
          {data.booking.unit} · {fmt(data.booking.arrival)} – {fmt(data.booking.departure)}
        </p>
        <p className="text-sm text-muted pt-1">
          {de
            ? 'Das Meldegesetz verpflichtet Ihren Gastgeber, diese Angaben für jede reisende Person zu erfassen. Wenn Sie sie jetzt eintragen, geht es bei der Ankunft schneller.'
            : 'Your host is required by law to record these details for every traveller. Filling them in now means less paperwork on arrival.'}
        </p>
      </Card>

      {people.map((p, i) => (
        <Card key={i} className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="font-medium flex-1">
              {i === 0
                ? (de ? 'Meldende Person' : 'Lead traveller')
                : `${de ? 'Mitreisende Person' : 'Traveller'} ${i + 1}`}
            </h2>
            {people.length > 1 && (
              <button
                onClick={() => setPeople(people.filter((_, j) => j !== i))}
                className="text-muted"
                aria-label={de ? 'Entfernen' : 'Remove'}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Vorname' : 'First name'}>
              <Input value={p.first_name} autoComplete="given-name"
                onChange={(e) => setPerson(i, { first_name: e.target.value })} />
            </Field>
            <Field label={de ? 'Nachname' : 'Last name'}>
              <Input value={p.last_name} autoComplete="family-name"
                onChange={(e) => setPerson(i, { last_name: e.target.value })} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Geburtsdatum' : 'Date of birth'}>
              <Input type="date" value={p.birth_date}
                onChange={(e) => setPerson(i, { birth_date: e.target.value })} />
            </Field>
            <Field label={de ? 'Staatsangehörigkeit' : 'Nationality'}>
              <Input value={p.nationality}
                onChange={(e) => setPerson(i, { nationality: e.target.value })} />
            </Field>
          </div>

          {/* Anschrift nur bei der meldenden Person: Mitreisende teilen sie in aller
              Regel, und zehn Adressfelder auf einem Handy füllt niemand aus. */}
          {i === 0 && (
            <>
              <Field label={de ? 'Straße und Hausnummer' : 'Street and number'}>
                <Input value={p.street} autoComplete="street-address"
                  onChange={(e) => setPerson(i, { street: e.target.value })} />
              </Field>
              <div className="grid grid-cols-[7rem_1fr] gap-3">
                <Field label={de ? 'PLZ' : 'Postcode'}>
                  <Input value={p.postcode} autoComplete="postal-code"
                    onChange={(e) => setPerson(i, { postcode: e.target.value })} />
                </Field>
                <Field label={de ? 'Ort' : 'Town'}>
                  <Input value={p.city} autoComplete="address-level2"
                    onChange={(e) => setPerson(i, { city: e.target.value })} />
                </Field>
              </div>
              <Field label={de ? 'Land' : 'Country'}>
                <Input value={p.country} autoComplete="country-name"
                  onChange={(e) => setPerson(i, { country: e.target.value })} />
              </Field>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Ausweisart' : 'Document type'}>
              <Select value={p.document_type}
                onChange={(e) => setPerson(i, { document_type: e.target.value })}>
                <option value="">{de ? '– keine Angabe –' : '– not given –'}</option>
                <option value="passport">{de ? 'Reisepass' : 'Passport'}</option>
                <option value="id_card">{de ? 'Personalausweis' : 'ID card'}</option>
                <option value="driving_licence">{de ? 'Führerschein' : 'Driving licence'}</option>
              </Select>
            </Field>
            <Field label={de ? 'Ausweisnummer' : 'Document number'}>
              <Input value={p.document_number}
                onChange={(e) => setPerson(i, { document_number: e.target.value })} />
            </Field>
          </div>
        </Card>
      ))}

      <Button variant="secondary" className="w-full"
        onClick={() => setPeople([...people, emptyPerson()])}>
        <Plus size={16} /> {de ? 'Weitere Person' : 'Add traveller'}
      </Button>

      <Card className="p-4 space-y-3">
        <Field
          label={de ? 'Voraussichtliche Ankunftszeit' : 'Expected arrival time'}
          hint={de ? 'Hilft Ihrem Gastgeber bei der Übergabe' : 'Helps your host plan the handover'}
        >
          <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} />
        </Field>
        <Field label={de ? 'Anmerkung' : 'Anything we should know'}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </Card>

      {error && (
        <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>
      )}

      <Button variant="primary" className="w-full" disabled={busy} onClick={submit}>
        {busy
          ? (de ? 'Wird gesendet …' : 'Sending …')
          : (de ? 'Check-in abschließen' : 'Complete check-in')}
      </Button>

      <p className="text-xs text-muted text-center pb-6">
        {data.host.name} · {data.host.street} · {data.host.postcode} {data.host.city}
        {data.host.email && <> · {data.host.email}</>}
      </p>
    </Shell>
  )
}

function Shell({ children, host }: { children: React.ReactNode; host?: string }) {
  return (
    <div className={cx('min-h-dvh bg-surface-2')}>
      <div className="max-w-lg mx-auto p-4 space-y-3 pt-safe">
        {host && (
          <div className="py-2 text-center font-semibold">{host}</div>
        )}
        {children}
      </div>
    </div>
  )
}
